/**
 * Request/response contracts for the dashboard API's fixed query catalog.
 * The API never accepts raw KQL from the browser — every endpoint here maps
 * to one parameterized query defined in dashboard/api/src/queries.
 */
import { z } from 'zod';
/**
 * Matches the collector's Get-StableHash output (16-hex hash, `hashUserName:
 * true`) or a real Windows username/domain\user (`hashUserName: false`) —
 * see collector/config/config.schema.json. The dashboard is now behind login
 * (see auth.ts), so displaying the real identifier is acceptable.
 */
export const developerIdSchema = z
    .string()
    .regex(/^[0-9a-f]{16}$|^[\w.\-@\\]{1,100}$/, 'developerId must be a hash or a valid username');
/**
 * Which collector's data a request targets. Three independent collectors,
 * three independent event families (see shared/src/telemetry.ts) — a
 * per-tool request never mixes them (the "All tools" overview endpoint is
 * the one deliberate exception — see overviewSummarySchema below). Defaults
 * to 'copilot' server-side (see api/src/routes/dashboard.ts) for backward
 * compatibility with callers that predate this field.
 */
export const toolSchema = z.enum(['copilot', 'claude-code', 'codex', 'kiro', 'gemini', 'cursor', 'windsurf']);
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
/** True for an ISO 8601 date or date-time naming a real calendar day (not 2026-02-31). */
function isIsoDate(value) {
    const match = ISO_DATE_PATTERN.exec(value);
    if (!match || Number.isNaN(Date.parse(value)))
        return false;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const calendar = new Date(Date.UTC(year, month - 1, day));
    return calendar.getUTCFullYear() === year && calendar.getUTCMonth() === month - 1 && calendar.getUTCDate() === day;
}
/** An ISO 8601 date (`2026-10-05`) or date-time. Anything else is a 400, never an Invalid Date in a query. */
const isoDateSchema = z.string().max(40).refine(isIsoDate, { message: 'Must be an ISO 8601 date or date-time' });
/** A query value that may be sent once or repeated (`?models=a&models=b`). */
function listParam(item, max) {
    return z.preprocess((value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]), z.array(item).max(max).optional());
}
/** Same shape as settings' modelIdSchema; kept here because settings.ts imports this file. */
export const modelFilterSchema = z.string().regex(/^[\w.:@/+-]{1,100}$/, 'Invalid model id');
/**
 * Project and surface values reach KQL as verbatim (@'…') literals, so the
 * single quote (which would end one) and control characters are rejected.
 */
export const projectFilterSchema = z.string().regex(/^[^'"\\\u0000-\u001f]{1,200}$/, 'Invalid project id');
export const surfaceFilterSchema = z.string().regex(/^[^'"\\\u0000-\u001f]{1,100}$/, 'Invalid surface');
/** Only token-priced tools can be filtered on the cost views. */
export const pricedToolSchema = z.enum(['claude-code', 'codex']);
export const dateRangeQuerySchema = z.object({
    /** ISO 8601 date, inclusive. Defaults to 30 days before `to`. Bounds are enforced server-side (api/src/dateRange.ts). */
    from: isoDateSchema.optional(),
    /** ISO 8601 date, inclusive. Defaults to now. */
    to: isoDateSchema.optional(),
    /** Optional filter to one developer's pseudonymous hash — see developerIdSchema. */
    developerId: developerIdSchema.optional(),
    /** Optional list used by team filters; each value is still a validated developer id. */
    developerIds: z.preprocess((value) => value === undefined ? undefined : Array.isArray(value) ? value : [value], z.array(developerIdSchema).max(500).optional()),
    /** Which collector's data to query. Defaults to 'copilot' when omitted. */
    tool: toolSchema.optional(),
    /** Spend, Efficiency and Sessions views only: limit to these tools, models, projects, or Codex surfaces. */
    tools: listParam(pricedToolSchema, 2),
    models: listParam(modelFilterSchema, 50),
    projects: listParam(projectFilterSchema, 50),
    surfaces: listParam(surfaceFilterSchema, 20),
});
/** Server-side limits the web app needs before it offers a time range. */
export const dashboardLimitsSchema = z.object({
    /**
     * Days of telemetry the workspace retains. A range reaching further back
     * returns only what is left, so the UI disables or labels such ranges.
     */
    maxLookbackDays: z.number().int().positive(),
});
export const adoptionByModelPointSchema = z.object({
    model: z.string(),
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
});
export const adoptionByEditorPointSchema = z.object({
    editor: z.string(), // "VSCode" | "VisualStudio"
    activeDevices: z.number(),
    totalEvents: z.number(),
});
export const adoptionTimeSeriesPointSchema = z.object({
    date: z.string(), // ISO date, day bucket
    metricName: z.string(),
    value: z.number(),
});
/** Premium quota (AIU) consumed per model — the licensing-cost metric. */
export const premiumQuotaByModelPointSchema = z.object({
    model: z.string(),
    aiu: z.number(),
    calls: z.number(),
    aiuPerCall: z.number(),
    indicativeCost: z.number(),
    indicativeCostPerCall: z.number(),
});
export const premiumQuotaSchema = z.object({
    totalAiu: z.number(),
    totalIndicativeCost: z.number(),
    currency: z.string().length(3),
    indicativeCostPerAiu: z.number().nonnegative(),
    indicativeCostSource: z.string(),
    isIndicative: z.literal(true),
    byModel: z.array(premiumQuotaByModelPointSchema),
});
/** Input vs output token volume across the range — the "context weight" signal. */
export const contextWeightSchema = z.object({
    inputTokens: z.number(),
    outputTokens: z.number(),
    /** inputTokens / outputTokens, rounded; 0 when outputTokens is 0. */
    ratio: z.number(),
});
/**
 * Copilot Chat request success rate, derived from CopilotUsageSnapshot's
 * `copilot.chat.requests.succeeded`/`.failed` counters (max-per-device-per-day,
 * summed — see queryAdoptionTimeSeries), which in turn come from each VS Code
 * Copilot Chat request's own logged status (success vs. error/cancelled).
 * Replaces the former shown/accepted inline-completion rate, which current
 * Copilot versions no longer log reliably. `status` is `unavailable` when
 * there were no chat requests in range, rather than publishing a nonsensical
 * rate.
 */
export const chatSuccessRateSchema = z.object({
    status: z.enum(['ok', 'unavailable']),
    succeeded: z.number(),
    failed: z.number(),
    ratePercent: z.number().optional(),
    reason: z.string().optional(),
});
export const dataQualityFindingSchema = z.object({
    severity: z.enum(['blocker', 'high', 'info']),
    message: z.string(),
});
/**
 * One (model, developer, editor) row for the drill-down table. `editor`
 * attributes the usage to the originating IDE — "VS Code", "Visual Studio",
 * or "Unknown" — and is never merged, so a developer who uses both appears
 * as two rows.
 */
export const modelDeveloperBreakdownPointSchema = z.object({
    model: z.string(),
    developerId: developerIdSchema,
    editor: z.string(), // "VS Code" | "Visual Studio" | "Unknown"
    events: z.number(),
    aiu: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    firstSeen: z.string(), // ISO 8601
    lastSeen: z.string(), // ISO 8601
});
/** Cache read-vs-creation token volume across the range — the "cache efficiency" signal. Claude Code only; Copilot has no cache concept. */
export const cacheEfficiencySchema = z.object({
    cacheCreationTokens: z.number(),
    cacheReadTokens: z.number(),
    /** cacheReadTokens / (cacheReadTokens + cacheCreationTokens), 0-1; 0 when both are 0. */
    cacheHitRatio: z.number(),
});
/** One model's share of a token-priced indicative cost (Claude Code, Codex). */
export const modelCostPointSchema = z.object({
    model: z.string(),
    /** The Settings pricing row this model matched, or null when the fallback rate was used. */
    pricedAs: z.string().nullable(),
    /** Uncached input tokens actually priced at the input rate (Codex: inputTokens - cachedInputTokens). */
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number(),
    cacheWriteTokens: z.number(),
    cost: z.number(),
});
/**
 * Indicative token cost — the same "indicative, not an invoice" pattern as
 * Copilot's premiumQuotaSchema, but priced per model from the admin-managed
 * pricing table (Settings → Model pricing), with a fallback rate for models
 * that have no row. See dashboard/README.md "Indicative cost".
 */
export const tokenIndicativeCostSchema = z.object({
    totalCost: z.number(),
    currency: z.string().length(3),
    source: z.string(),
    /** When Settings → Model pricing was last saved (or the built-in list's check date). */
    pricesReviewedAt: z.string(),
    /** Last review is older than PRICES_STALE_AFTER_DAYS. */
    pricesStale: z.boolean(),
    isIndicative: z.literal(true),
    byModel: z.array(modelCostPointSchema),
    /** Models in range priced at the fallback rate because they have no pricing row. */
    unpricedModels: z.array(z.string()),
});
/** Claude Code's indicative cost — cache reads and cache writes are priced separately from input. */
export const claudeCodeIndicativeCostSchema = tokenIndicativeCostSchema;
/**
 * One project's usage — requirement: per-project breakdown. projectId is a
 * salted 16-hex hash by default, or the readable project name (repository
 * `owner/repo`, or a folder name) when the collector opts in with
 * `sanitization.sendRawProjectId` — never a full path (see collector/docs/PRIVACY.md).
 */
export const projectBreakdownPointSchema = z.object({
    projectId: z.string().min(1).max(200),
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    firstSeen: z.string(), // ISO 8601
    lastSeen: z.string(), // ISO 8601
});
/**
 * One (developer, branch, model, day) row for the "Measurements by repository"
 * table. Shared shape across tools because it only carries input/output
 * token totals, but every row comes from ONE tool's catalog — rows are never
 * merged across tools (see dashboard/AGENTS.md "API rules"). `branch` is the
 * raw git branch name the collector sends only when its
 * `sanitization.sendGitBranch` opt-in is on; records without one are left
 * out rather than shown as "unknown". `repository` is the `owner/repo` name
 * the collector sends only with its `sanitization.sendGitRepository` opt-in;
 * empty when not sent.
 */
export const branchBreakdownPointSchema = z.object({
    developerId: developerIdSchema,
    repository: z.string(),
    branch: z.string(),
    model: z.string(),
    date: z.string(), // ISO date (UTC day bucket), YYYY-MM-DD
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
});
/** One (model, developer) pair for Claude Code's drill-down table — no `aiu`, adds cache + thinking tokens. */
export const claudeCodeModelDeveloperBreakdownPointSchema = z.object({
    model: z.string(),
    developerId: developerIdSchema,
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheCreationTokens: z.number(),
    cacheReadTokens: z.number(),
    thinkingTokens: z.number(),
    firstSeen: z.string(), // ISO 8601
    lastSeen: z.string(), // ISO 8601
});
/**
 * One model's usage for Claude Code — the counterpart to
 * adoptionByModelPointSchema, but with cache/thinking token totals Copilot
 * has no equivalent of. Kept separate from the Copilot schema for the same
 * reason as copilotToolSummarySchema/claudeCodeToolSummarySchema: a shared
 * shape would force one tool's fields to show as a misleading zero on the
 * other's chart.
 */
export const claudeCodeByModelPointSchema = z.object({
    model: z.string(),
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheCreationTokens: z.number(),
    cacheReadTokens: z.number(),
    thinkingTokens: z.number(),
});
/**
 * Session/active-time activity totals for the range — computed the same
 * cumulative-gauge-corrected way as the rest of ClaudeCodeUsageSnapshot's
 * counters (max per device per day, then summed; see
 * collector/docs/ARCHITECTURE.md "The Claude Code collector"). Surfaced as
 * its own totals rather than making the UI re-derive them from timeSeries,
 * so the number shown in a KPI card matches what a query directly computed.
 */
export const claudeCodeActivitySchema = z.object({
    sessions: z.number(),
    activeMinutes: z.number(),
});
export const reportingCoverageSchema = z.object({
    expectedDevices: z.number(),
    reportingDevices: z.number(),
    coveragePercent: z.number(),
    windowHours: z.number(),
});
/**
 * One developer, identified by the `userId`/`UserName` value the collector
 * emits — a hash or a real username depending on collector config (see
 * developerIdSchema). Requires authentication to view (see auth.ts).
 */
export const developerSummarySchema = z.object({
    developerId: developerIdSchema,
    events: z.number(),
    devices: z.number(),
    lastActive: z.string(), // ISO 8601
});
/**
 * Copilot-specific summary fields. Deliberately NOT merged into one shape
 * with Claude Code's — Copilot has chat-success-rate/AIU/editor-breakdown
 * signals Claude Code doesn't, and Claude Code has cache/project/session
 * signals Copilot doesn't. A single shared shape would force whichever tool
 * lacks a field to show a misleading zero instead of omitting it.
 */
export const copilotToolSummarySchema = z.object({
    tool: z.literal('copilot'),
    byModel: z.array(adoptionByModelPointSchema),
    byEditor: z.array(adoptionByEditorPointSchema),
    timeSeries: z.array(adoptionTimeSeriesPointSchema),
    premiumQuota: premiumQuotaSchema,
    contextWeight: contextWeightSchema,
    chatSuccessRate: chatSuccessRateSchema,
    modelDeveloperBreakdown: z.array(modelDeveloperBreakdownPointSchema),
    byBranch: z.array(branchBreakdownPointSchema),
});
/** Claude Code-specific summary fields — see copilotToolSummarySchema's comment for why this is separate. */
export const claudeCodeToolSummarySchema = z.object({
    tool: z.literal('claude-code'),
    byModel: z.array(claudeCodeByModelPointSchema),
    timeSeries: z.array(adoptionTimeSeriesPointSchema),
    activity: claudeCodeActivitySchema,
    cacheEfficiency: cacheEfficiencySchema,
    contextWeight: contextWeightSchema,
    /** Total extended-thinking tokens across the range — Copilot has no equivalent signal. */
    thinkingTokens: z.number(),
    indicativeCost: claudeCodeIndicativeCostSchema,
    byProject: z.array(projectBreakdownPointSchema),
    modelDeveloperBreakdown: z.array(claudeCodeModelDeveloperBreakdownPointSchema),
    byBranch: z.array(branchBreakdownPointSchema),
});
/** One (model, developer) pair for Codex's drill-down table. `aiu` has no Codex equivalent; adds cached/reasoning tokens and effort. */
export const codexModelDeveloperBreakdownPointSchema = z.object({
    model: z.string(),
    developerId: developerIdSchema,
    events: z.number(),
    inputTokens: z.number(),
    cachedInputTokens: z.number(),
    outputTokens: z.number(),
    reasoningTokens: z.number(),
    firstSeen: z.string(), // ISO 8601
    lastSeen: z.string(), // ISO 8601
});
/**
 * One model's usage for Codex. Kept separate from the Copilot/Claude Code
 * shapes for the same reason as copilotToolSummarySchema/
 * claudeCodeToolSummarySchema — a shared shape would force one tool's
 * fields to show as a misleading zero on another's chart.
 */
export const codexByModelPointSchema = z.object({
    model: z.string(),
    events: z.number(),
    inputTokens: z.number(),
    cachedInputTokens: z.number(),
    outputTokens: z.number(),
    reasoningTokens: z.number(),
});
/** Token volume by Codex's reasoning-effort setting — a signal neither other tool has. */
export const codexByEffortPointSchema = z.object({
    effort: z.string(), // "low" | "medium" | "high" | "unknown"
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
});
/** Requests by originating surface (Codex Desktop, CLI, IDE extension) — the Codex analog of Copilot's byEditor. */
export const codexBySurfacePointSchema = z.object({
    surface: z.string(),
    activeDevices: z.number(),
    totalEvents: z.number(),
});
/**
 * Codex's cache-efficiency signal. NOT the same formula as Claude Code's
 * cacheEfficiencySchema: Codex's cachedInputTokens is INCLUSIVE of
 * inputTokens (cacheHitRatio = cachedInputTokens / inputTokens), whereas
 * Claude Code's cache-read tokens are additive/separate from input tokens.
 * See collector/docs/ARCHITECTURE.md "The Codex collector".
 */
export const codexCacheEfficiencySchema = z.object({
    inputTokens: z.number(),
    cachedInputTokens: z.number(),
    /** cachedInputTokens / inputTokens, 0-1; 0 when inputTokens is 0. */
    cacheHitRatio: z.number(),
});
/**
 * Codex's quota-headroom signal — Copilot and Claude Code have no
 * equivalent. `usedPercent` fields are point-in-time gauges (last-observed
 * across reporting devices in the range), never summed or max()'d like the
 * cumulative counters elsewhere in this file. `status` is `unavailable`
 * when collection.agents.codex.includeRateLimits was not enabled anywhere
 * in the range, rather than publishing a misleading zero.
 */
export const codexRateLimitSchema = z.object({
    status: z.enum(['ok', 'unavailable']),
    primaryUsedPercent: z.number().optional(),
    primaryWindowMinutes: z.number().optional(),
    secondaryUsedPercent: z.number().optional(),
    secondaryWindowMinutes: z.number().optional(),
    planType: z.string().optional(),
    observedAt: z.string().optional(), // ISO 8601 of the last-observed gauge reading
});
/**
 * Codex's indicative cost. Priced on BILLABLE input tokens (inputTokens -
 * cachedInputTokens) plus cached input at the cache-read rate, since Codex's
 * inputTokens already includes cached tokens — see
 * codexCacheEfficiencySchema's comment.
 */
export const codexIndicativeCostSchema = tokenIndicativeCostSchema;
/** Codex session/active-time activity totals for the range, plus the subagent-delegation signal neither other tool has. */
export const codexActivitySchema = z.object({
    sessions: z.number(),
    activeMinutes: z.number(),
    /** Share (0-1) of all requests that came from a spawned subagent thread rather than the root session. */
    subagentShare: z.number(),
});
/** Codex-specific summary fields. Deliberately NOT merged into one shape with Copilot's/Claude Code's — see copilotToolSummarySchema's comment for why. */
export const codexToolSummarySchema = z.object({
    tool: z.literal('codex'),
    byModel: z.array(codexByModelPointSchema),
    byEffort: z.array(codexByEffortPointSchema),
    bySurface: z.array(codexBySurfacePointSchema),
    timeSeries: z.array(adoptionTimeSeriesPointSchema),
    activity: codexActivitySchema,
    rateLimit: codexRateLimitSchema,
    cacheEfficiency: codexCacheEfficiencySchema,
    contextWeight: contextWeightSchema,
    indicativeCost: codexIndicativeCostSchema,
    modelDeveloperBreakdown: z.array(codexModelDeveloperBreakdownPointSchema),
    byBranch: z.array(branchBreakdownPointSchema),
});
/*
 * Kiro (AWS). Kiro writes no token counts, so every token figure below is an
 * ESTIMATE (visible text characters / 4 — see collector/docs/ARCHITECTURE.md
 * "The Kiro collector"). The fields are named `estimated*` so they are never
 * read as, or summed with, another tool's measured tokens, and Kiro has no
 * indicative cost: it bills in plan credits, not tokens.
 */
/** One model's Kiro usage, in estimated tokens. */
export const kiroByModelPointSchema = z.object({
    model: z.string(),
    events: z.number(),
    estimatedInputTokens: z.number(),
    estimatedOutputTokens: z.number(),
});
/** Turns by surface: Kiro IDE or Kiro CLI. */
export const kiroBySurfacePointSchema = z.object({
    surface: z.string(),
    activeDevices: z.number(),
    totalEvents: z.number(),
});
/** Turns by Kiro agent mode (vibe / spec / other; empty for the CLI, shown as "CLI"), with the autopilot share. */
export const kiroByAgentModePointSchema = z.object({
    agentMode: z.string(),
    events: z.number(),
    autopilotEvents: z.number(),
});
export const KIRO_TOOL_CATEGORIES = ['Shell', 'Filesystem', 'Search', 'Browser', 'Retrieval', 'Other'];
export const kiroToolCategorySchema = z.enum(KIRO_TOOL_CATEGORIES);
/** Tool calls by category. The collector never sends raw tool names. */
export const kiroToolCategoryPointSchema = z.object({
    category: kiroToolCategorySchema,
    calls: z.number(),
});
/** Kiro session/active-time activity totals for the range, plus tool-call volume and failure share. */
export const kiroActivitySchema = z.object({
    sessions: z.number(),
    activeMinutes: z.number(),
    toolCalls: z.number(),
    /** Share (0-1) of tool calls whose result reported failure; 0 when there were no tool calls. */
    toolErrorRate: z.number(),
});
/** One (model, developer) pair for Kiro's drill-down table, in estimated tokens. */
export const kiroModelDeveloperBreakdownPointSchema = z.object({
    model: z.string(),
    developerId: z.string(),
    events: z.number(),
    estimatedInputTokens: z.number(),
    estimatedOutputTokens: z.number(),
    toolCalls: z.number(),
    firstSeen: z.string(),
    lastSeen: z.string(),
});
/** Kiro-specific summary fields — see copilotToolSummarySchema's comment for why this is separate. */
export const kiroToolSummarySchema = z.object({
    tool: z.literal('kiro'),
    byModel: z.array(kiroByModelPointSchema),
    bySurface: z.array(kiroBySurfacePointSchema),
    byAgentMode: z.array(kiroByAgentModePointSchema),
    toolCategories: z.array(kiroToolCategoryPointSchema),
    timeSeries: z.array(adoptionTimeSeriesPointSchema),
    activity: kiroActivitySchema,
    modelDeveloperBreakdown: z.array(kiroModelDeveloperBreakdownPointSchema),
});
/**
 * Google Gemini CLI (collector 1.6.0+). The collector normalises Gemini's
 * token counts to the Codex convention — inputTokens INCLUDES
 * cachedInputTokens, outputTokens includes reasoningTokens — so the Codex
 * point shapes, cache formula and billable-input pricing apply unchanged.
 */
export const geminiActivitySchema = z.object({
    sessions: z.number(),
    activeMinutes: z.number(),
});
export const geminiToolSummarySchema = z.object({
    tool: z.literal('gemini'),
    byModel: z.array(codexByModelPointSchema),
    timeSeries: z.array(adoptionTimeSeriesPointSchema),
    activity: geminiActivitySchema,
    cacheEfficiency: codexCacheEfficiencySchema,
    indicativeCost: tokenIndicativeCostSchema,
    modelDeveloperBreakdown: z.array(codexModelDeveloperBreakdownPointSchema),
});
/*
 * Cursor IDE (collector 1.6.0+, off by default). Token counts are Cursor's own
 * per-message `tokenCount`, summed per request, but not yet confirmed on a real
 * install (TokenBasis `reported-unverified`), so they are shown with that
 * caveat and never priced: Cursor bills through its own plans.
 */
export const cursorByModelPointSchema = z.object({
    model: z.string(),
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
});
export const cursorActivitySchema = z.object({
    sessions: z.number(),
    activeMinutes: z.number(),
    toolCalls: z.number(),
    /** Share (0-1) of tool calls that were refused or failed; 0 when there were none. */
    toolErrorRate: z.number(),
    /** Requests with a non-zero token count, so the page can say how far the token figures are filled in. */
    requestsWithTokens: z.number(),
    requests: z.number(),
});
export const cursorModelDeveloperBreakdownPointSchema = z.object({
    model: z.string(),
    developerId: z.string(),
    events: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    toolCalls: z.number(),
    firstSeen: z.string(),
    lastSeen: z.string(),
});
export const cursorToolSummarySchema = z.object({
    tool: z.literal('cursor'),
    byModel: z.array(cursorByModelPointSchema),
    timeSeries: z.array(adoptionTimeSeriesPointSchema),
    activity: cursorActivitySchema,
    modelDeveloperBreakdown: z.array(cursorModelDeveloperBreakdownPointSchema),
});
/*
 * Windsurf / Devin Desktop (collector 1.6.0+, off by default). Activity only: the
 * collector counts Cascade conversation files by last-write time, so there are no
 * tokens, models, tool calls or per-request records, and nothing is priced.
 */
export const windsurfActivitySchema = z.object({
    /** Conversations written in range (per-device daily max, summed). */
    sessions: z.number(),
    /** Device-days with Windsurf activity (per-device daily max of windsurf.active.days, summed). */
    activeDays: z.number(),
});
export const windsurfToolSummarySchema = z.object({
    tool: z.literal('windsurf'),
    timeSeries: z.array(adoptionTimeSeriesPointSchema),
    activity: windsurfActivitySchema,
});
export const toolSummarySchema = z.discriminatedUnion('tool', [
    copilotToolSummarySchema,
    claudeCodeToolSummarySchema,
    codexToolSummarySchema,
    kiroToolSummarySchema,
    geminiToolSummarySchema,
    cursorToolSummarySchema,
    windsurfToolSummarySchema,
]);
/**
 * One tool's row in the "All tools" overview comparison
 * (`/api/dashboard/overview`). Deliberately limited to measures that are
 * genuinely comparable across tools with different token-accounting rules —
 * see dashboard/AGENTS.md "API rules" for why raw token counts are excluded
 * here. `indicativeCost` reuses each tool's own cost query/config, so the
 * $/Mtok assumptions stay tool-specific even though the totals are shown
 * side by side.
 */
export const toolComparisonPointSchema = z.object({
    tool: toolSchema,
    activeDevelopers: z.number(),
    requests: z.number(),
    activeMinutes: z.number(),
    /**
     * Indicative token cost, or null when the tool has no cost figure that can
     * be compared as spend. Copilot is null: its only cost signal is a legacy
     * AIU estimate, which is not how GitHub bills (AI credits with an included
     * allowance), so it is shown on the Copilot tab only and never as spend.
     */
    indicativeCost: z.number().nullable(),
    currency: z.string().length(3),
});
export const overviewSummarySchema = z.object({
    range: dateRangeQuerySchema,
    byTool: z.array(toolComparisonPointSchema),
    generatedAt: z.string(),
});
/** How a cost view was priced, so it can say what the figure is and isn't (E1.11). */
export const pricingBasisSchema = z.object({
    /** Settings → Cost "cost basis" text. */
    source: z.string(),
    /** When Settings → Model pricing was last saved (or the built-in list's check date). */
    reviewedAt: z.string(),
    /** Last review is older than PRICES_STALE_AFTER_DAYS. */
    stale: z.boolean(),
    /** Part of the total priced at the fallback rate because the model has no pricing row. */
    fallbackCost: z.number(),
});
/**
 * Spend view (`/api/dashboard/spend`). Like the overview, it puts tools side
 * by side, but only as indicative cost — each tool's tokens are priced by its
 * own catalog first, so no raw token count is ever merged across tools.
 * Copilot has no spend figure (see toolComparisonPointSchema) and appears with
 * a null cost.
 */
export const tokenTypeCostSchema = z.object({
    /** Uncached input (Codex: billable input, excluding cached input). */
    input: z.number(),
    output: z.number(),
    cacheRead: z.number(),
    /** Claude Code cache creation; always 0 for Codex, which has no cache-write charge. */
    cacheWrite: z.number(),
});
export const spendToolPointSchema = z.object({
    tool: toolSchema,
    /** null = not included as spend (Copilot). */
    cost: z.number().nullable(),
    /** Same tool's cost in the previous equal-length period; null when not comparable. */
    previousCost: z.number().nullable(),
});
export const spendModelPointSchema = z.object({
    tool: toolSchema,
    model: z.string(),
    /** The Settings pricing row this model matched, or null when the fallback rate was used. */
    pricedAs: z.string().nullable(),
    /** Fast, mid, or frontier — from the pricing row, else its output price (see resolveModelTier). */
    tier: z.enum(['fast', 'mid', 'frontier']),
    cost: z.number(),
    byType: tokenTypeCostSchema,
    /** The tokens behind byType, in the same terms (Codex input is billable input). Used by the what-if simulator. */
    tokens: z.object({ input: z.number(), output: z.number(), cacheRead: z.number(), cacheWrite: z.number() }),
    /** Distinct sessions that used this model in the range. */
    sessions: z.number(),
});
/**
 * Budget for the current calendar month, UTC (`/api/dashboard/budget`, E1.8).
 * Claude Code and Codex indicative cost only; filters and the date range don't apply.
 */
export const budgetTeamPointSchema = z.object({
    team: z.string(),
    /** null = no budget set for this group. */
    budget: z.number().nullable(),
    monthToDate: z.number(),
    projected: z.number().nullable(),
    developers: z.number(),
});
export const budgetStatusSchema = z.object({
    currency: z.string().length(3),
    month: z.string(), // YYYY-MM
    /** First day with data: the 1st, or later when the month starts before retention. */
    from: z.string(),
    /** Data does not reach back to the 1st, so month-to-date is understated and the run rate uses the days available. */
    partial: z.boolean(),
    daysInMonth: z.number(),
    /** Days of the month covered so far, including the part of today that has passed. */
    daysElapsed: z.number(),
    budget: z.number().nullable(),
    monthToDate: z.number(),
    /** Month-end at the month's run rate so far; null on the month's first hours. */
    projected: z.number().nullable(),
    byTool: z.array(z.object({ tool: toolSchema, monthToDate: z.number() })),
    /** Cost per UTC day, for the cumulative chart. */
    daily: z.array(z.object({ date: z.string(), cost: z.number() })),
    activeDevelopers: z.number(),
    /** Teams with at least the minimum team size; smaller teams share "Other" (Executive settings). */
    teams: z.array(budgetTeamPointSchema),
    /** Teams with a budget whose activity is not shown because they are below the minimum team size. */
    hiddenTeams: z.array(z.string()),
    pricing: pricingBasisSchema,
    generatedAt: z.string(),
});
/** One tool and model's cost on one UTC day. Days without usage are omitted. */
export const spendDailyPointSchema = z.object({
    date: z.string(), // YYYY-MM-DD
    tool: toolSchema,
    model: z.string(),
    cost: z.number(),
});
/** projectId is the collector's hashed project key, or the readable name when opted in — see projectBreakdownPointSchema. */
export const spendProjectPointSchema = z.object({
    tool: toolSchema,
    projectId: z.string(),
    requests: z.number(),
    cost: z.number(),
});
/** A session is identified only by its salted hash. Deliberately carries no developer id (no per-developer ranking). */
export const spendSessionPointSchema = z.object({
    tool: toolSchema,
    sessionId: z.string(),
    projectId: z.string().nullable(),
    requests: z.number(),
    cost: z.number(),
    firstSeen: z.string(), // ISO 8601
    lastSeen: z.string(), // ISO 8601
});
export const spendSummarySchema = z.object({
    range: dateRangeQuerySchema,
    currency: z.string().length(3),
    source: z.string(),
    pricing: pricingBasisSchema,
    isIndicative: z.literal(true),
    totalCost: z.number(),
    /** Total for the previous equal-length period; null when it is not comparable. */
    previousTotalCost: z.number().nullable(),
    /** Why the previous period is not comparable (e.g. beyond retention); null when it is. */
    comparisonUnavailableReason: z.string().nullable(),
    byTool: z.array(spendToolPointSchema),
    byModel: z.array(spendModelPointSchema),
    daily: z.array(spendDailyPointSchema),
    topProjects: z.array(spendProjectPointSchema),
    topSessions: z.array(spendSessionPointSchema),
    /** Models priced at the fallback rate because they have no pricing row. */
    unpricedModels: z.array(z.string()),
    generatedAt: z.string(),
});
/**
 * Efficiency ratios for one group of one tool's usage (`/api/dashboard/efficiency`).
 * Each tool's ratios use that tool's own token definitions (see each field), so
 * tools are shown side by side but never summed. null = no denominator in range.
 */
export const efficiencyMetricsSchema = z.object({
    requests: z.number(),
    /** Indicative cost, priced as on the Spend view. */
    cost: z.number(),
    outputTokens: z.number(),
    /** Output tokens per unit of indicative cost. */
    outputPerCost: z.number().nullable(),
    /** Thinking (Claude Code) or reasoning (Codex) tokens ÷ output tokens; both are part of output. */
    reasoningRatio: z.number().nullable(),
    /** All context sent per output token. Claude Code: input + cache read + cache write; Codex: input (which includes cached). */
    contextLoad: z.number().nullable(),
    /** Claude Code: cache read ÷ (cache read + cache write). Codex: cached input ÷ input. Same as each tool's own page. */
    cacheHitRatio: z.number().nullable(),
});
export const efficiencySummarySchema = z.object({
    range: dateRangeQuerySchema,
    currency: z.string().length(3),
    pricing: pricingBasisSchema,
    byTool: z.array(efficiencyMetricsSchema.extend({ tool: toolSchema })),
    /** Per UTC day and tool; days without usage are omitted. */
    daily: z.array(efficiencyMetricsSchema.extend({ date: z.string(), tool: toolSchema })),
    byModel: z.array(efficiencyMetricsSchema.extend({ tool: toolSchema, model: z.string(), pricedAs: z.string().nullable() })),
    /**
     * Per team (Settings → Developers) and tool. Teams with fewer configured
     * members than the minimum team size share "Other"; a group still below the
     * minimum is left out, so no individual can be singled out.
     */
    byTeam: z.array(efficiencyMetricsSchema.extend({ team: z.string(), tool: toolSchema, developers: z.number() })),
    minTeamSize: z.number(),
    unpricedModels: z.array(z.string()),
    generatedAt: z.string(),
});
/** One model's part of a session (`/api/dashboard/sessions`). */
export const sessionModelPointSchema = z.object({
    model: z.string(),
    pricedAs: z.string().nullable(),
    requests: z.number(),
    cost: z.number(),
    /** Same token terms as SpendModelPoint.tokens, plus thinking (Claude Code) or reasoning (Codex) tokens, which are part of output. */
    tokens: z.object({ input: z.number(), output: z.number(), cacheRead: z.number(), cacheWrite: z.number(), reasoning: z.number() }),
    /** Largest context sent in one turn: Claude Code input + cache read + cache write; Codex input (cached included). */
    maxContextTokens: z.number(),
    /** The model's context window in tokens (Settings → Model pricing, else the price feed); null when unknown. */
    contextWindow: z.number().nullable(),
});
export const runawayReasonSchema = z.enum(['cost', 'duration', 'requests']);
/** One session, identified by its salted hash, with the developer who ran it. */
export const sessionPointSchema = z.object({
    tool: toolSchema,
    sessionId: z.string(),
    /** The collector's user id (hash or username); display names are applied in the web app. null if not sent. */
    developerId: z.string().nullable(),
    projectId: z.string().nullable(),
    requests: z.number(),
    /** Codex requests from spawned subagent threads; null for Claude Code, which does not record them separately. */
    subagentRequests: z.number().nullable(),
    cost: z.number(),
    durationMinutes: z.number(),
    firstSeen: z.string(),
    lastSeen: z.string(),
    /** Measures on which this session is an extreme outlier for its tool. Empty = not runaway. */
    runawayReasons: z.array(runawayReasonSchema),
    /** Largest single-turn context in the session, and as a share of that model's window (null when no window is known). */
    peakContextTokens: z.number(),
    peakContextPercent: z.number().nullable(),
    byModel: z.array(sessionModelPointSchema),
});
/** Distribution of one tool's sessions. Thresholds are null when there are too few sessions to judge outliers. */
export const sessionStatsSchema = z.object({
    tool: toolSchema,
    sessions: z.number(),
    totalCost: z.number(),
    medianCost: z.number(),
    p95Cost: z.number(),
    medianDurationMinutes: z.number(),
    medianRequests: z.number(),
    runaway: z.number(),
    thresholds: z.object({ cost: z.number(), durationMinutes: z.number(), requests: z.number() }).nullable(),
});
/**
 * How full the context window was, turn by turn, for one tool (E1.13). Turns
 * are binned by context size in the query, so shares are approximate.
 */
export const contextPressureSchema = z.object({
    tool: toolSchema,
    turns: z.number(),
    /** Bands by percent of the model's window; `to` null = above 100%. Only turns whose model has a known window. */
    bands: z.array(z.object({ from: z.number(), to: z.number().nullable(), turns: z.number() })),
    /** Turns at or above CONTEXT_NEAR_LIMIT_PERCENT of the window. */
    nearLimitTurns: z.number(),
    /** Turns whose model has no known context window. */
    unknownWindowTurns: z.number(),
    modelsWithoutWindow: z.array(z.string()),
});
export const CONTEXT_NEAR_LIMIT_PERCENT = 80;
/** One spawned Codex subagent thread (identified only by its hash). */
export const subagentRunSchema = z.object({
    threadId: z.string(),
    sessionId: z.string(),
    models: z.array(z.string()),
    requests: z.number(),
    cost: z.number(),
    firstSeen: z.string(),
    lastSeen: z.string(),
});
/**
 * Subagent analytics (E1.14, E2.6): turns from spawned subagent threads against
 * the root sessions that started them, for one tool. Claude Code is covered by
 * records from collector 1.6.0+, which mark subagent turns.
 */
export const subagentSummarySchema = z.object({
    tool: toolSchema,
    rootCost: z.number(),
    subagentCost: z.number(),
    rootRequests: z.number(),
    subagentRequests: z.number(),
    /** Distinct subagent threads. */
    runs: z.number(),
    /** Mean and median cost of one run; null with no runs. */
    costPerRun: z.number().nullable(),
    medianRunCost: z.number().nullable(),
    sessions: z.number(),
    sessionsWithSubagents: z.number(),
    daily: z.array(z.object({ date: z.string(), rootCost: z.number(), subagentCost: z.number() })),
    byModel: z.array(z.object({ model: z.string(), runs: z.number(), requests: z.number(), cost: z.number() })),
    /** Costliest runs, up to SUBAGENT_RUN_LIMIT. */
    topRuns: z.array(subagentRunSchema),
});
/**
 * One GitHub Copilot session (E2.10, collector 1.6.0+). No cost: Copilot is
 * not priced per token and stays out of spend, so runaway uses duration and
 * requests only.
 */
export const copilotSessionPointSchema = z.object({
    sessionId: z.string(),
    developerId: z.string().nullable(),
    /** "VS Code" or "Copilot CLI". */
    surface: z.string(),
    models: z.array(z.string()),
    requests: z.number(),
    firstSeen: z.string(),
    lastSeen: z.string(),
    durationMinutes: z.number(),
    runawayReasons: z.array(z.enum(['duration', 'requests'])),
});
export const copilotSessionsSummarySchema = z.object({
    sessions: z.number(),
    requests: z.number(),
    medianRequests: z.number(),
    medianDurationMinutes: z.number(),
    runaway: z.number(),
    /** null below MIN_SESSIONS_FOR_OUTLIERS sessions. */
    thresholds: z.object({ durationMinutes: z.number(), requests: z.number() }).nullable(),
    bySurface: z.array(z.object({ surface: z.string(), sessions: z.number(), requests: z.number() })),
    /** Every runaway session plus the busiest others, by requests, capped at the session list limit. */
    list: z.array(copilotSessionPointSchema),
});
const toolCallCountsSchema = z.object({
    calls: z.number(),
    /** Calls whose outcome is known (Codex often reports none). */
    knownOutcomes: z.number(),
    failed: z.number(),
    /** Calls that used the same tool as the previous call in the session. */
    repeats: z.number(),
});
/**
 * Tool, MCP server and skill usage for Claude Code and Codex (E2.5, collector
 * 1.6.0+ with the `sendToolNames` opt-in). Aggregate only — no per-developer ranking.
 */
export const toolCallsSummarySchema = toolCallCountsSchema.extend({
    sessions: z.number(),
    bySource: z.array(toolCallCountsSchema.extend({ tool: z.enum(['claude-code', 'codex']) })),
    /** Busiest tools, by calls, capped at TOOL_CALL_LIST_LIMIT. */
    byTool: z.array(toolCallCountsSchema.extend({ tool: z.enum(['claude-code', 'codex']), toolName: z.string(), mcpServer: z.string(), sessions: z.number() })),
    byMcpServer: z.array(toolCallCountsSchema.extend({ mcpServer: z.string(), tools: z.number(), sessions: z.number() })),
    bySkill: z.array(z.object({ skillName: z.string(), calls: z.number(), sessions: z.number() })),
});
export const sessionsSummarySchema = z.object({
    range: dateRangeQuerySchema,
    currency: z.string().length(3),
    pricing: pricingBasisSchema,
    stats: z.array(sessionStatsSchema),
    /** Every runaway session plus the costliest others, sorted by cost, capped at `limit`. */
    sessions: z.array(sessionPointSchema),
    limit: z.number(),
    /** Sessions in range, before the cap. */
    totalSessions: z.number(),
    contextPressure: z.array(contextPressureSchema),
    /** One per tool with session records that mark subagent turns; empty when none do. */
    subagents: z.array(subagentSummarySchema),
    /** GitHub Copilot sessions; null when a tool, model, project or surface filter is set (they don't apply to Copilot). */
    copilot: copilotSessionsSummarySchema.nullable(),
    /** Claude Code / Codex tool calls; null when a model, project or surface filter is set (tool calls carry none). */
    toolCalls: toolCallsSummarySchema.nullable(),
    unpricedModels: z.array(z.string()),
    generatedAt: z.string(),
});
/** Values the Spend, Efficiency and Sessions filters can offer for a range (`/api/dashboard/filter-options`). */
export const filterOptionsSchema = z.object({
    models: z.array(z.object({ tool: pricedToolSchema, model: z.string(), requests: z.number() })),
    projects: z.array(z.object({ tool: pricedToolSchema, projectId: z.string(), requests: z.number() })),
    /** Claude Code records carry a surface from collector 1.6.0; older ones carry none. */
    surfaces: z.array(z.object({ tool: pricedToolSchema, surface: z.string(), requests: z.number() })),
});
/** RTK's byte-derived token estimates, kept distinct from model usage and billing tokens. */
export const tokenOptimizationDailyPointSchema = z.object({
    date: z.string(),
    commands: z.number(),
    estimatedRawOutputTokens: z.number(),
    estimatedFilteredOutputTokens: z.number(),
    estimatedSavedTokens: z.number(),
    reductionPercent: z.number(),
    /** Wall-clock time of RTK-wrapped commands; 0 for days sent before collector 1.2.0. */
    execTimeMs: z.number(),
});
export const tokenOptimizationDeveloperPointSchema = tokenOptimizationDailyPointSchema.omit({ date: true }).extend({
    developerId: developerIdSchema,
    peakDailyDevices: z.number(),
    lastSeen: z.string(),
});
export const tokenOptimizationWindowPointSchema = z.object({
    developerId: developerIdSchema,
    window: z.enum(['today', 'last_7_days', 'last_30_days']),
    calls: z.number(),
    tokensSaved: z.number(),
    tokensBefore: z.number(),
    savingsPercent: z.number(),
    devices: z.number(),
    lastSeen: z.string(),
    /**
     * The most recent source day contributing to this aggregate (empty when no
     * device reported). Headroom's windows are rolling, not daily history, and
     * only devices within a day of this date are summed — see
     * queryHeadroomOptimization's `mostRecentSourceDate` filter — so this is
     * also the date the aggregate should be labeled "as of" in the UI.
     */
    asOfDate: z.string(),
});
/** Aggregated Graphify benchmark, explicitly not an observed savings count. */
export const graphifyBenchmarkPointSchema = z.object({
    developerId: developerIdSchema,
    devices: z.number(),
    benchmarkedRoots: z.number(),
    averageCorpusTokens: z.number(),
    averageQueryTokens: z.number(),
    averageEstimatedDeltaPerQuery: z.number(),
    /** averageCorpusTokens ÷ averageQueryTokens (ratio of means), 0 when nothing was benchmarked. */
    averageReductionRatio: z.number(),
    totalNodes: z.number(),
    totalEdges: z.number(),
    /** Roots with a graphify-out/cost.json record. */
    buildRoots: z.number(),
    /** Measured lifetime tokens Graphify reported spending to build the graphs — a real cost, unlike the benchmark. */
    buildInputTokens: z.number(),
    buildOutputTokens: z.number(),
    buildRuns: z.number(),
    lastSeen: z.string(),
    /** Most recent source day contributing to this row — see queryGraphifyBenchmark. */
    asOfDate: z.string(),
});
/** RTK totals across the selected range and every reporting developer. */
export const rtkTotalsSchema = tokenOptimizationDailyPointSchema.omit({ date: true });
/** One Headroom window summed across developers (each already gated to devices near the latest report). */
export const headroomWindowTotalSchema = tokenOptimizationWindowPointSchema
    .omit({ developerId: true, devices: true, lastSeen: true, asOfDate: true })
    .extend({ developers: z.number() });
/** Graphify across developers: benchmark averages weighted by benchmarked roots, build cost summed. */
export const graphifyTotalsSchema = graphifyBenchmarkPointSchema
    .omit({ developerId: true, lastSeen: true, asOfDate: true })
    .extend({ developers: z.number() });
/**
 * How many developers active with an AI coding tool in the range (any of
 * Copilot, Claude Code, Codex, Kiro, with at least one request — the Executive
 * view's definition) also reported each optimization tool. A count of
 * developers only; no token figures cross the adoption/optimization boundary.
 */
export const optimizationCoverageSchema = z.object({
    activeAiDevelopers: z.number(),
    rtk: z.number(),
    headroom: z.number(),
    graphify: z.number(),
});
export const tokenOptimizationSummarySchema = z.object({
    range: dateRangeQuerySchema,
    daily: z.array(tokenOptimizationDailyPointSchema),
    developers: z.array(tokenOptimizationDeveloperPointSchema),
    rtkTotals: rtkTotalsSchema,
    headroom: z.array(tokenOptimizationWindowPointSchema),
    headroomTotals: z.array(headroomWindowTotalSchema),
    /** Latest source day behind the Headroom figures; empty when none reported. */
    headroomAsOfDate: z.string(),
    graphify: z.array(graphifyBenchmarkPointSchema),
    /** Null when no developer reported Graphify in range. */
    graphifyTotals: graphifyTotalsSchema.nullable(),
    graphifyAsOfDate: z.string(),
    coverage: optimizationCoverageSchema,
    generatedAt: z.string(),
});
/** RTK totals for one calendar week (Monday start, like `rtk gain --weekly`) or month. */
export const rtkPeriodPointSchema = rtkTotalsSchema.extend({
    /** YYYY-MM-DD of the week's Monday, or YYYY-MM for a month. */
    period: z.string(),
    /** Last day of the period (YYYY-MM-DD). */
    periodEnd: z.string(),
});
/**
 * RTK's all-time totals from each device's latest lifetime snapshot in the
 * range, summed across devices. Unlike the daily history (capped at the
 * collector's 90-day window) this covers everything RTK has recorded.
 */
export const rtkLifetimeSchema = z.object({
    commands: z.number(),
    estimatedRawOutputTokens: z.number(),
    estimatedFilteredOutputTokens: z.number(),
    estimatedSavedTokens: z.number(),
    reductionPercent: z.number(),
    execTimeMs: z.number(),
    devices: z.number(),
    asOfDate: z.string(),
});
/** Commands RTK could not filter and passed through raw, and how many it recovered. */
export const rtkParseFailuresSchema = z.object({
    failures: z.number(),
    /** Failure-weighted mean of each device's reported recovery rate. */
    recoveryPercent: z.number(),
    devices: z.number(),
});
/**
 * One allow-listed command family (e.g. `rtk git diff`) from each device's
 * latest top-command table, summed across devices. RTK only reports its top
 * 10 command labels, so this is a partial view of all commands.
 */
export const rtkCommandFamilyPointSchema = z.object({
    family: z.string(),
    commands: z.number(),
    estimatedSavedTokens: z.number(),
    /** Command-weighted mean of RTK's per-command average savings. */
    savingsPercent: z.number(),
    avgTimeMs: z.number(),
    developers: z.number(),
});
export const rtkDeveloperLifetimeSchema = rtkLifetimeSchema.omit({ asOfDate: true }).extend({
    developerId: developerIdSchema,
    parseFailures: z.number().nullable(),
    recoveryPercent: z.number().nullable(),
});
/** The dedicated RTK view. Every figure is RTK's own byte-derived estimate. */
export const rtkDetailSummarySchema = z.object({
    range: dateRangeQuerySchema,
    totals: rtkTotalsSchema,
    daily: z.array(tokenOptimizationDailyPointSchema),
    weekly: z.array(rtkPeriodPointSchema),
    monthly: z.array(rtkPeriodPointSchema),
    developers: z.array(tokenOptimizationDeveloperPointSchema),
    /** Null until a collector 1.2.0+ lifetime snapshot is reported in range. */
    lifetime: rtkLifetimeSchema.nullable(),
    lifetimeByDeveloper: z.array(rtkDeveloperLifetimeSchema),
    parseFailures: rtkParseFailuresSchema.nullable(),
    /** Empty unless some device opted in to collection.agents.rtk.includeCommandBreakdown. */
    commandFamilies: z.array(rtkCommandFamilyPointSchema),
    commandFamiliesAsOfDate: z.string(),
    coverage: optimizationCoverageSchema.pick({ activeAiDevelopers: true, rtk: true }),
    generatedAt: z.string(),
});
export const dashboardSummarySchema = z.object({
    range: dateRangeQuerySchema,
    developers: z.array(developerSummarySchema),
    coverage: reportingCoverageSchema,
    dataQuality: z.array(dataQualityFindingSchema),
    generatedAt: z.string(),
    toolData: toolSummarySchema,
});
/**
 * The Executive view (`/api/dashboard/executive`) — leadership metrics on one
 * screen, also exported as a single slide. Aggregate-only by design: no
 * developer ids or names appear anywhere in this response, so the slide can
 * circulate without exposing individuals (small teams are merged into
 * "Other" — see ExecutiveSettings.minTeamSize).
 *
 * Like the overview, it compares only normalized measures across tools
 * (active developers, active days, requests, active minutes, indicative
 * cost) — never raw token counts. "Active" means the developer made at
 * least one request that day, not merely that the collector reported.
 */
const periodComparisonSchema = z.object({
    current: z.number(),
    previous: z.number(),
});
/** A percentage that may be undefined (e.g. no active developers or no requests in a period). */
const percentComparisonSchema = z.object({
    current: z.number().nullable(),
    previous: z.number().nullable(),
});
export const executiveTrendPointSchema = z.object({
    /** ISO date (UTC) the rolling 28-day window ends on. */
    weekEnding: z.string(),
    allTools: z.number(),
    copilot: z.number(),
    claudeCode: z.number(),
    codex: z.number(),
    kiro: z.number(),
    gemini: z.number(),
    cursor: z.number(),
    windsurf: z.number(),
});
export const executiveToolPointSchema = z.object({
    tool: toolSchema,
    activeDevelopers: z.number(),
    /** Indicative token spend, or null for a tool excluded from spend (see ExecutiveSummary.spend.excludedTools). */
    spend: z.number().nullable(),
    spendPerDeveloper: z.number().nullable(),
});
export const executiveTeamPointSchema = z.object({
    /** Team name from Settings → Developers, or "Other" for merged small teams. */
    team: z.string(),
    /** Non-excluded developers assigned to the team in settings. */
    members: z.number(),
    /** Members active in the period. */
    active: z.number(),
    adoptionPercent: z.number(),
});
export const executiveDepthSegmentSchema = z.object({
    key: z.enum(['habitual', 'regular', 'trying']),
    label: z.string(),
    /** Human description of the band, e.g. "13+ active days". */
    description: z.string(),
    current: z.number(),
    previous: z.number(),
});
export const executiveInsightSchema = z.object({
    kind: z.enum(['positive', 'attention', 'note']),
    text: z.string(),
});
export const executiveSummarySchema = z.object({
    range: z.object({ from: z.string(), to: z.string() }),
    previousRange: z.object({ from: z.string(), to: z.string() }),
    /**
     * Whether the previous period can be compared. It cannot when it reaches
     * back past the workspace's data retention: part of it has expired, so its
     * totals would be understated and every delta inflated. When `available`
     * is false the previous period is not queried — percentage `previous`
     * fields are null, count and money `previous` fields are 0 placeholders,
     * and none of them may be displayed or used for a delta.
     */
    comparison: z.object({ available: z.boolean(), reason: z.string().nullable() }),
    /** Monday–Friday days in the current period. */
    workingDays: z.number(),
    /** Active days needed to count as habitual in the current period. */
    habitualDays: z.number(),
    activeDevelopers: periodComparisonSchema,
    adoption: z.object({
        current: z.number().nullable(),
        previous: z.number().nullable(),
        denominator: z.number(),
        /** 'configured' = Settings → Executive; 'known' = developers seen in retained telemetry (up to 90 days). */
        denominatorSource: z.enum(['configured', 'known']),
        targetPercent: z.number(),
    }),
    habitual: percentComparisonSchema.extend({ count: z.number() }),
    activeHours: periodComparisonSchema.extend({ perDeveloper: z.number() }),
    /**
     * Indicative token spend for the tools that are priced per token (Claude
     * Code, Codex). `perDeveloper` divides by developers active on those tools,
     * not by all active developers.
     */
    spend: periodComparisonSchema.extend({
        perDeveloper: z.number(),
        previousPerDeveloper: z.number(),
        currency: z.string().length(3),
        source: z.string(),
        /**
         * Tools left out of every spend figure because their cost cannot be
         * stated as current spend. Copilot: GitHub bills AI credits with an
         * included allowance, and no billing feed is connected yet.
         */
        excludedTools: z.array(toolSchema),
    }),
    agenticShare: percentComparisonSchema,
    trend: z.array(executiveTrendPointSchema),
    /** Active developers needed to meet the target, or null when the denominator is unknown. */
    targetDevelopers: z.number().nullable(),
    tools: z.array(executiveToolPointSchema),
    /** Active developers who used more than one tool in the period. */
    multiToolDevelopers: z.number(),
    teams: z.array(executiveTeamPointSchema),
    /** Active developers with no team (or excluded from team figures) in settings. */
    unassignedActive: z.number(),
    depth: z.array(executiveDepthSegmentSchema),
    insights: z.array(executiveInsightSchema),
    coverage: reportingCoverageSchema,
    generatedAt: z.string(),
});
/**
 * Data health (GET /api/dashboard/health): how complete and how fresh the
 * telemetry behind the dashboard is, per source. Describes the pipeline, not
 * usage — no token or cost figure is merged across sources here.
 */
export const healthSourceSchema = z.enum(['copilot', 'claude-code', 'codex', 'kiro', 'gemini', 'cursor', 'windsurf', 'rtk', 'headroom', 'graphify']);
/**
 * The operating system a collector reports on its CollectorRun events. The
 * collector sends one of the first four fixed words; `unknown` is the
 * dashboard's own bucket for a missing or unexpected value.
 */
export const collectorPlatformSchema = z.enum(['windows', 'macos', 'linux', 'other', 'unknown']);
/**
 * - `reporting`      telemetry arrived from this source recently
 * - `stale`          devices reported before, none in the recent window
 * - `failing`        every device's latest collector run failed
 * - `disabled`       every device that reports a run has the source switched off
 * - `not-installed`  every device that reports a run does not have the source
 * - `no-report`      nothing has been received and no collector run says why
 */
export const sourceStateSchema = z.enum(['reporting', 'stale', 'failing', 'disabled', 'not-installed', 'no-report']);
export const collectorRunHealthSchema = z.object({
    /** Devices that sent at least one CollectorRun event for this source in the range. */
    devices: z.number(),
    /** Devices by the status of their latest run. */
    ok: z.number(),
    partial: z.number(),
    failed: z.number(),
    disabled: z.number(),
    notInstalled: z.number(),
    otherSkipped: z.number(),
    runs: z.number(),
    /** Runs in the range where ingestion accepted only part of what was sent. */
    partialRuns: z.number(),
    failedRuns: z.number(),
    /** Items ingestion permanently rejected, summed over runs in the range. */
    itemsRejected: z.number(),
    lastRun: z.string().nullable(),
    lastSuccessfulSend: z.string().nullable(),
});
export const sourceHealthSchema = z.object({
    source: healthSourceSchema,
    state: sourceStateSchema,
    /** Devices reporting in the last `windowHours`, out of devices seen in the prior `basisDays` days. */
    devices: z.object({ reporting: z.number(), expected: z.number() }),
    /** Latest usage telemetry in the selected range (event time), or null when there is none. */
    lastObserved: z.string().nullable(),
    /** When that telemetry reached the workspace. */
    lastIngested: z.string().nullable(),
    /** Minutes between an event being built on the device and reaching the workspace. */
    lagMinutes: z.object({ p50: z.number().nullable(), p95: z.number().nullable() }),
    events: z.number(),
    /** Share of events that repeat an identifier already seen. Queries de-duplicate these; null when none carry an identifier. */
    duplicatePercent: z.number().nullable(),
    /** Share of events with no developer or no device identifier. */
    missingIdentifierPercent: z.number().nullable(),
    /** Share of token cost priced at the fallback rate (Claude Code and Codex only). */
    fallbackCostPercent: z.number().nullable(),
    /** Null until a device on collector 1.4.1+ reports a run for this source. */
    runs: collectorRunHealthSchema.nullable(),
});
export const confidenceLevelSchema = z.enum(['high', 'medium', 'low']);
/** The Executive view's data-confidence indicator: four measures and an overall level. */
export const dataConfidenceSchema = z.object({
    level: confidenceLevelSchema,
    /**
     * Reporting coverage across the three AI tools, counted per device per
     * tool: a device that uses two tools counts twice, so one whose Claude Code
     * collector has stopped lowers the figure even while its Copilot collector
     * reports. The same figure as the Executive summary's `coverage`.
     */
    coverage: z.object({ percent: z.number().nullable(), reporting: z.number(), expected: z.number(), level: confidenceLevelSchema }),
    /** Hours since the latest telemetry of any AI tool. */
    recency: z.object({ hours: z.number().nullable(), level: confidenceLevelSchema }),
    /** Share of token cost priced at the fallback rate rather than a model's own price. */
    fallbackCost: z.object({ percent: z.number().nullable(), level: confidenceLevelSchema }),
    /** Active developers who have a profile (name or team) in Settings. */
    identity: z.object({ percent: z.number().nullable(), matched: z.number(), active: z.number(), level: confidenceLevelSchema }),
});
export const dataHealthSchema = z.object({
    range: z.object({ from: z.string(), to: z.string() }),
    generatedAt: z.string(),
    /** When the underlying queries last ran — earlier than `generatedAt` when served from cache. */
    queriedAt: z.string(),
    /** Latest usage telemetry of any AI tool in the range. */
    dataAsOf: z.string().nullable(),
    /** How "expected devices" is defined: devices seen in the prior `basisDays` days; "reporting" = seen in the last `windowHours`. */
    deviceBasis: z.object({ basisDays: z.number(), windowHours: z.number() }),
    /** Distinct devices reporting any source recently, out of distinct devices seen in the prior days. */
    devices: z.object({ reporting: z.number(), expected: z.number() }),
    sources: z.array(sourceHealthSchema),
    /** Devices by collector version, newest first. */
    collectorVersions: z.array(z.object({ version: z.string(), devices: z.number() })),
    /** Devices by the platform their latest CollectorRun in the range reported, in a fixed order; empty with no run reports. */
    devicesByPlatform: z.array(z.object({ platform: collectorPlatformSchema, devices: z.number() })),
    confidence: dataConfidenceSchema,
});
/**
 * Developer-level collector health (GET /api/dashboard/health/developers):
 * for each developer, when their machine's collector last ran and last got
 * data through. Built from CollectorRun events, so it needs collector 1.4.1+.
 * Pipeline facts only — no usage figure.
 *
 * Skipped runs (not installed, switched off, nothing new) and the older-collector flag are
 * informational and never affect the state.
 * - `healthy`     the collector ran recently and at least one active source succeeded (or none failed)
 * - `failing`     no active source succeeded and at least one failed
 * - `stale`       no collector run (or, without run reports, no telemetry) for longer than the threshold
 * - `no-run-reports` telemetry arrives but the machine sends no CollectorRun events (collector older than 1.4.1)
 */
export const developerHealthStateSchema = z.enum(['healthy', 'failing', 'stale', 'no-run-reports']);
export const collectorRunStatusSchema = z.enum(['ok', 'partial', 'failed', 'skipped']);
export const developerSourceRunSchema = z.object({
    source: healthSourceSchema,
    status: collectorRunStatusSchema,
    skipReason: z.string().nullable(),
    lastRun: z.string(),
    lastSuccessfulSend: z.string().nullable(),
    runs: z.number(),
    failedRuns: z.number(),
});
export const developerHealthSchema = z.object({
    developerId: developerIdSchema,
    state: developerHealthStateSchema,
    /** A machine sends telemetry without run reports (collector < 1.4.1). A warning only; never overrides `state`. */
    olderCollector: z.boolean(),
    /** Latest CollectorRun event from this developer's machine(s), any source. Null without run reports. */
    lastRun: z.string().nullable(),
    /** Latest time any source got data through (collector-reported). Null when unknown. */
    lastSuccessfulSend: z.string().nullable(),
    /** Latest usage telemetry that reached the workspace. */
    lastTelemetry: z.string().nullable(),
    devices: z.number(),
    collectorVersion: z.string().nullable(),
    runs: z.number(),
    failedRuns: z.number(),
    partialRuns: z.number(),
    sources: z.array(developerSourceRunSchema),
});
export const developerHealthSummarySchema = z.object({
    generatedAt: z.string(),
    /** Runs and telemetry are read from the last `basisDays` days, measured back from now. */
    basisDays: z.number(),
    /** A developer with nothing for longer than this is `stale`. From Settings → Data health. */
    staleAfterHours: z.number(),
    developers: z.array(developerHealthSchema),
});
/** One collector execution (GET /api/dashboard/health/developers/runs). */
export const developerRunHistoryEntrySchema = z.object({
    at: z.string(),
    source: healthSourceSchema,
    status: collectorRunStatusSchema,
    skipReason: z.string().nullable(),
    /** Hashed machine name, as the collector sends it. */
    device: z.string().nullable(),
    collectorVersion: z.string().nullable(),
    durationMs: z.number().nullable(),
    itemsSent: z.number().nullable(),
    itemsAccepted: z.number().nullable(),
    itemsRejected: z.number().nullable(),
});
export const developerRunHistorySchema = z.object({
    developerId: developerIdSchema,
    range: z.object({ from: z.string(), to: z.string() }),
    /** Newest first. */
    runs: z.array(developerRunHistoryEntrySchema),
    /** True when more runs matched than the list holds. */
    truncated: z.boolean(),
});
//# sourceMappingURL=api.js.map